import { memo, useCallback } from 'react';
import { Focusable } from 'react-aria-components';

import ReviewPredictionsTooltipBanner from '@/assets/review-predictions-tooltip-banner.svg?react';
import { merge } from '@/components';
import { Flex, Heading, Text, Tooltip, TooltipProps, TooltipTrigger } from '@/components/core';
import { CloseIcon } from '@/components/icons';
import { useTour } from '@/hooks';
import { OmitChildren } from '@/utils';

export type ReviewPredictionsTooltipProps = OmitChildren<TooltipProps>;

const IReviewPredictionsTooltip = ({ placement = 'left', className, ...rest }: ReviewPredictionsTooltipProps) => {
  const { showConfirmPredictionsTooltip, setShowConfirmPredictionsTooltip } = useTour();
  const close = useCallback(() => setShowConfirmPredictionsTooltip(false), [setShowConfirmPredictionsTooltip]);

  return (
    <TooltipTrigger isDisabled={!showConfirmPredictionsTooltip} isOpen={showConfirmPredictionsTooltip}>
      <Tooltip
        className={merge('flex max-w-[16rem] flex-col gap-0 border border-gray-300 p-3 shadow-md', className)}
        placement={placement}
        {...rest}
      >
        <Flex className="items-center">
          <Heading size="sm">Confirm to improve</Heading>
          <div className="grow" />
          <button className="cursor-pointer" onClick={close}>
            <CloseIcon className="!text-gray-500" />
          </button>
        </Flex>

        <ReviewPredictionsTooltipBanner className="w-full" />

        <Text size="xs">Corrections help your agent become smarter and more accurate next time.</Text>

        <button
          className="mt-2 flex w-fit cursor-pointer items-center self-end rounded-sm bg-gray-800 px-2 py-1"
          onClick={close}
        >
          <Text size="xs" className="font-semibold text-white">
            Got it!
          </Text>
        </button>
      </Tooltip>
      <Focusable>
        <div role="button" tabIndex={-1} />
      </Focusable>
    </TooltipTrigger>
  );
};

export const ReviewPredictionsTooltip = memo(IReviewPredictionsTooltip) as typeof IReviewPredictionsTooltip;
